import { Menu } from 'components/Menu/Menu'
import { Key, useEffect } from 'react'
import copy from 'copy-to-clipboard'
import { useAbility } from '@casl/react'

import { useRootStore } from 'stores/RootStore'
import { AbilityContext } from 'components/Can'
import { Section } from '@react-stately/collections'
import { ArrowOut, Link, Delete, Download, Edit } from 'almanac-ui/icons'
import useOpenWindowSafely from 'hooks/useOpenWindowSafely'
import { AlmanacFile, UserRole } from 'types'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { useFinderContext } from 'screens/Finder/FinderContext'
import { useModals } from 'almanac-ui/Modal'
import { ConfirmDeleteMediaFileModal } from 'components/ConfirmDeleteMediaFileModal/ConfirmDeleteMediaFileModal'
import { useConfirmDeleteMediaFile } from './hooks/useConfirmDeleteMediaFile'
import { useConfirmDeleteDesign } from './hooks/useConfirmDeleteDesign'
import { useDownloadMediaFile } from 'hooks/useDownloadMediaFile'
import { ItemElement } from '@react-types/shared'
import { REACT_APP_FRONTEND_URL } from 'env'
import { useWorkspace } from 'hooks/useWorkspace'
import { SubjectType } from 'utils/caslAbility'
import { MenuProps } from 'components/Menu/Types'
import { Item } from 'components/Menu/MenuItem'

interface Props {
  file: AlmanacFile
  close: () => void
  onRename?: () => void
  menuType?: MenuProps<any>['menuType']
}

export const MediaFileMenu = ({ file, close, onRename, menuType = 'dropdown' }: Props) => {
  const openWindowSafely = useOpenWindowSafely()
  const { openModal, closeModal } = useModals()
  const { onConfirm: onConfirmMedia, isProcessing: isProcessingMedia } = useConfirmDeleteMediaFile(file)
  const { onConfirm: onConfirmDesign, isProcessing: isProcessingDesign } = useConfirmDeleteDesign(file)
  const { onDeleteRequest } = useFinderContext()
  const { downloadFile } = useDownloadMediaFile()
  const isMediaFile = file.type === 'media_file'
  const isDesign = file.type === 'design'
  const onConfirm = isMediaFile ? onConfirmMedia : onConfirmDesign
  const isProcessing = isMediaFile ? isProcessingMedia : isProcessingDesign
  const workspace = useWorkspace(file.real_workspace_id)
  const { files } = useRootStore()

  useEffect(() => {
    files.fetch(file.id)
  }, [file.id, files])

  const fileSummary = files.get(file.id)

  const ability = useAbility(AbilityContext)
  const authedFile = {
    role: workspace?.role || UserRole.UNAUTHED,
    version_control_mode: false,
    permissions: fileSummary?.permissions,
    __caslSubjectType__: SubjectType.ALMANAC_FILE,
  }

  const can: typeof ability.can = (action, subject) => {
    return ability.can(action, subject)
  }

  const handleAction = async (action: Key) => {
    if (action === 'openInNewTab') {
      if (isMediaFile) openWindowSafely(`${REACT_APP_FRONTEND_URL}/media_files/${file.id}`)
      if (isDesign) openWindowSafely(`${REACT_APP_FRONTEND_URL}${file.url}`)

      close()
    }

    if (action === 'copyLink') {
      if (isMediaFile) copy(`${REACT_APP_FRONTEND_URL}/media_files/${file.id}`)
      if (isDesign) copy(`${REACT_APP_FRONTEND_URL}${file.url}`)
      showToastMessage('Link copied')
    }

    if (action === 'downloadFile') {
      close()
      downloadFile(file)
    }

    if (action === 'rename') {
      onRename?.()
      close()
    }

    if (action === 'delete') {
      close()

      if (isMediaFile || isDesign) {
        openModal(ConfirmDeleteMediaFileModal, {
          title: file.title,
          type: file.mime_type?.split('/')[0],
          onConfirm: async () => {
            const success = await onConfirm()
            if (success) {
              closeModal()
              onDeleteRequest([file])
            }
          },
          isProcessing,
        })
      }
    }
  }

  return (
    <Menu onAction={handleAction} aria-label="Media File Menu" menuType={menuType}>
      <Section>
        {
          [
            <Item key="openInNewTab" textValue="Open in new tab" frontIcon={ArrowOut}>
              Open in new tab
            </Item>,
          ].filter(Boolean) as ItemElement<HTMLElement>[]
        }
      </Section>
      <Section>
        {
          [
            <Item key="copyLink" textValue="Copy Link" frontIcon={Link}>
              Copy Link
            </Item>,
            isMediaFile && (
              <Item key="downloadFile" textValue="Download" frontIcon={Download}>
                Download
              </Item>
            ),
            onRename && can('rename', authedFile) && (
              <Item key="rename" textValue="Rename" frontIcon={Edit}>
                Rename
              </Item>
            ),
            <Item key="delete" textValue="Delete" frontIcon={Delete}>
              Delete
            </Item>,
          ].filter(Boolean) as ItemElement<HTMLElement>[]
        }
      </Section>
    </Menu>
  )
}
